import React from 'react';
import { Box, CircularProgress, Typography, styled } from '@mui/material';
import { ContainerLayout } from 'components/ContainerLayout/ContainerLayout';
import { useNavigate } from 'react-router';
import { PageHeader } from 'components/PageHeader/PageHeader';
import { WeightDetailsView } from 'components/WeightDetails/WeightDetailsView';
import { ErrorState } from 'components/ErrorState/ErrorState';
import UnexpectedErrorOne from 'assets/images/ErrorState/UnexpectedErrorOne.svg';
import { useWeightDetails } from 'features/progress/hooks/useWeightDetails';
import { useLoggedInUser } from 'features/auth/hooks/useAuthData';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';

const ScreenLayout = styled(Box)({
  '& > .MuiBox-root': {
    display: 'flex',
    flexDirection: 'column',
  },
  '& > .MuiBox-root > :first-child': {
    flexShrink: 0,
  },
});

const PageRoot = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  flex: '1 1 0%',
  minHeight: 0,
  overflowY: 'auto',
  backgroundColor: theme.palette.common.white,
  '& > *': {
    flexShrink: 0,
  },
}));

const ContentRegion = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  backgroundColor: theme.palette.common.white,
  [theme.breakpoints.up('md')]: {
    backgroundColor: theme.palette.brandBackgroundPrimary.main,
    '& > *': {
      width: '100%',
      maxWidth: theme.spacing(92.875),
      backgroundColor: theme.palette.common.white,
    },
  },
}));

const CenterState = styled(Box)(() => ({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

export const WeightDetails: React.FC = () => {
  const navigate = useNavigate();
  const user = useLoggedInUser();
  const { translation } = useTranslationContext();

  const {
    dateRange,
    dates,
    weights,
    latestValue,
    unit,
    readings,
    isLoading,
    isError,
    download,
    isDownloading,
  } = useWeightDetails(user?.id);

  const renderContent = () => {
    if (isLoading) {
      return (
        <CenterState>
          <CircularProgress aria-label="Loading weight data" />
        </CenterState>
      );
    }

    if (isError) {
      return (
        <CenterState>
          <ErrorState
            imageArray={[UnexpectedErrorOne]}
            title={translation('request_failed')}
            subtitle={translation('generic_failure_error')}
          />
        </CenterState>
      );
    }

    return (
      <WeightDetailsView
        dateRange={dateRange}
        dates={dates}
        weights={weights}
        latestValue={latestValue}
        unit={unit}
        readings={readings}
        onWidgetClick={() => undefined}
        onDownload={download}
        isDownloading={isDownloading}
      />
    );
  };

  return (
    <ScreenLayout>
      <ContainerLayout>
        <PageRoot>
          <PageHeader title={translation('weight')} isBackVisible onBack={() => navigate(-1)} />
          <ContentRegion>{renderContent()}</ContentRegion>
        </PageRoot>
      </ContainerLayout>
    </ScreenLayout>
  );
};
